import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/api';

// Tipos para el usuario
interface User {
  id: string;
  email: string;
  nombre: string;
  apellido: string;
  tipoSocio: string;
}

// Tipos para el contexto
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

// Crear el contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook personalizado para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// Props para el provider
interface AuthProviderProps {
  children: ReactNode;
}

// Provider del contexto
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Verificar si hay un token guardado al cargar la aplicación
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Configurar el token en axios
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Verificar si el token es válido obteniendo el perfil del usuario
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, []);


  // Función para obtener el perfil del usuario
  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/auth/profile`);
      setUser(response.data.user);
    } catch (error) {
      // Si hay error, limpiar el token y el usuario
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Función de login
  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${getApiBaseUrl()}/api/auth/login`, {
        email,
        password
      });

      const { token, user } = response.data;

      // Guardar el token en localStorage
      localStorage.setItem('token', token);

      // Configurar el token en axios para futuras peticiones
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Actualizar el estado del usuario
      setUser(user);
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Error al iniciar sesión');
      }
    }
  };

  // Función de logout
  const logout = () => {
    // Limpiar el token y el usuario
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Valor del contexto
  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
